import {
  useEffect, useState,
} from 'react';
import type { LatLng } from '~/_shared/types/latLng';
import { useThrottle } from '~/_shared/utils/hooks/useThrottle';
import { FPS60 } from '~/_shared/utils/throttle/throttle';
import type { MapObjectZIndex } from '../../mapObject.types';
import { useMapObjectManager } from '../../private/useMapObjectManager';

export const useAddCursorLabel = ({
  zIndex,
  getCursorLabel,
}: {
  zIndex: MapObjectZIndex;
  getCursorLabel?: (position: LatLng) => ({ text: string; angle?: number }) | null | undefined;
}) => {
  const manager = useMapObjectManager();

  const [cursorPosition, setCursorPosition] = useState<LatLng>();

  // Throttle mouse move event when drawing so it won't affect performance
  const onMouseMove = useThrottle((event: google.maps.MapMouseEvent) => {
    if (!event.latLng) {
      return;
    }

    const position = { lat: event.latLng.lat(), lng: event.latLng.lng() };

    setCursorPosition(position);

    const cursorLabelData = getCursorLabel?.(position);
    if (cursorLabelData) {
      manager.upsertCursorLabel(position, cursorLabelData.text, zIndex.outlineLabel, cursorLabelData.angle);
    }
    else {
      manager.removeCursorLabel();
    }
  }, [manager, zIndex.outlineLabel, getCursorLabel], FPS60);

  useEffect(() => { // Registers onMouseMove
    const moveListener = manager.addMapMouseMoveListener(onMouseMove);

    return () => { // Cleanup
      moveListener.remove();
      manager.removeCursorLabel();
      setCursorPosition(undefined);
    };
  }, [onMouseMove, manager]);

  return {
    cursorPosition,
  };
};
